// Precios Históricos de Materiales — buscador para Compras.
// Muestra: último precio, último proveedor, fecha, histórico de últimas 5 compras
// con variación % entre última y anterior.
import { useState, useCallback, Fragment } from 'react';
import api from '../../lib/api';
import { useRegisterCmdKCommands } from '../../contexts/CmdKContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Search, TrendingUp, TrendingDown, Minus, DollarSign, Package, Wrench,
  ChevronDown, ChevronRight, Loader2, History,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtCLP = (v) => new Intl.NumberFormat('es-CL').format(Math.round(v || 0));
const fmtFecha = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return iso; }
};

export default function PreciosHistoricosPage() {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [searched, setSearched] = useState(false);

  const search = useCallback(async (query = q) => {
    const qs = (query || '').trim();
    if (qs.length < 2) {
      toast.error('Escribí al menos 2 caracteres');
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await api.get('/bodega/precios-historicos', { params: { q: qs, limit: 50 } });
      setItems(res.data?.items || []);
      setExpanded(new Set());
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al buscar');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q]);

  const toggleExpand = (codigo) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo); else next.add(codigo);
      return next;
    });
  };

  // Cmd+K commands
  useRegisterCmdKCommands(
    'precios-historicos',
    'Precios Históricos',
    [
      { id: 'ph-clear', label: 'Limpiar búsqueda', icon: 'X',
        run: () => { setQ(''); setItems([]); setSearched(false); },
        enabled: !!q || items.length > 0 },
    ],
    [q, items.length],
  );

  const VariacionBadge = ({ pct }) => {
    if (pct === null || pct === undefined) return null;
    if (Math.abs(pct) < 0.5) {
      return (
        <Badge variant="outline" className="text-[10px] h-5 border-slate-300 text-slate-600">
          <Minus className="w-2.5 h-2.5 mr-0.5" /> {pct}%
        </Badge>
      );
    }
    const positive = pct > 0;
    return (
      <Badge
        variant="outline"
        className={`text-[10px] h-5 ${positive ? 'bg-red-50 text-red-700 border-red-300' : 'bg-emerald-50 text-emerald-700 border-emerald-300'}`}
      >
        {positive ? <TrendingUp className="w-2.5 h-2.5 mr-0.5" /> : <TrendingDown className="w-2.5 h-2.5 mr-0.5" />}
        {positive ? '+' : ''}{pct}%
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-3 md:p-6 space-y-4" data-testid="page-precios-historicos">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-emerald-600" />
          Precios Históricos de Materiales
        </h1>
        <p className="text-sm text-muted-foreground">
          Busca por código o descripción · muestra último precio, proveedor y evolución de los últimos 5 ingresos.
        </p>
      </div>

      {/* Buscador */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={(e) => { e.preventDefault(); search(); }} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input
                className="pl-8"
                placeholder="Ej: 'tornillo', 'soldadura', 'P-1234'…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
                data-testid="input-precios-search"
              />
            </div>
            <Button type="submit" disabled={loading} data-testid="btn-precios-search">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Search className="w-4 h-4 mr-1" />}
              Buscar
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Resultados */}
      {searched && !loading && items.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Sin resultados para "{q}".
          </CardContent>
        </Card>
      )}

      {items.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="table-precios-historicos">
                <thead className="bg-slate-50 dark:bg-slate-900 border-b text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="text-left px-2 py-2 w-6"></th>
                    <th className="text-left px-2 py-2">Código</th>
                    <th className="text-left px-2 py-2">Descripción</th>
                    <th className="text-right px-2 py-2">Stock</th>
                    <th className="text-right px-2 py-2">Último Precio</th>
                    <th className="text-left px-2 py-2">Último Proveedor</th>
                    <th className="text-left px-2 py-2">Fecha</th>
                    <th className="text-center px-2 py-2">Var. %</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const isOpen = expanded.has(it.codigo);
                    const hasHist = it.historial && it.historial.length > 0;
                    return (
                      <Fragment key={it.codigo}>
                        <tr
                          className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50 cursor-pointer"
                          onClick={() => hasHist && toggleExpand(it.codigo)}
                          data-testid={`row-precio-${it.codigo}`}
                        >
                          <td className="px-2 py-2">
                            {hasHist ? (isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />) : null}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs">
                            {it.es_herramienta ? (
                              <Wrench className="w-3 h-3 inline mr-1 text-amber-600" />
                            ) : (
                              <Package className="w-3 h-3 inline mr-1 text-blue-600" />
                            )}
                            {it.codigo}
                          </td>
                          <td className="px-2 py-2">{it.descripcion || '—'} <span className="text-[10px] text-muted-foreground">{it.unidad}</span></td>
                          <td className="px-2 py-2 text-right tabular-nums">{fmtCLP(it.stock_actual)}</td>
                          <td className="px-2 py-2 text-right font-semibold tabular-nums">
                            {it.ultimo_precio > 0 ? `$${fmtCLP(it.ultimo_precio)}` : '—'}
                          </td>
                          <td className="px-2 py-2 text-xs truncate max-w-[200px]" title={it.ultimo_proveedor_nombre}>
                            {it.ultimo_proveedor_nombre || <span className="text-muted-foreground italic">sin historial</span>}
                          </td>
                          <td className="px-2 py-2 text-xs font-mono">{fmtFecha(it.ultima_fecha)}</td>
                          <td className="px-2 py-2 text-center"><VariacionBadge pct={it.variacion_pct} /></td>
                        </tr>
                        {isOpen && hasHist && (
                          <tr className="bg-slate-50/60 dark:bg-slate-900/30" data-testid={`hist-${it.codigo}`}>
                            <td colSpan={8} className="px-4 py-3">
                              <div className="text-xs font-semibold mb-2 flex items-center gap-1">
                                <History className="w-3 h-3" /> Últimas {it.historial.length} compras
                              </div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="text-muted-foreground border-b">
                                      <th className="text-left py-1 pr-3">Fecha</th>
                                      <th className="text-left py-1 pr-3">OC</th>
                                      <th className="text-left py-1 pr-3">Recepción</th>
                                      <th className="text-left py-1 pr-3">Proveedor</th>
                                      <th className="text-right py-1 pr-3">Cant.</th>
                                      <th className="text-right py-1">Valor Unit.</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {it.historial.map((h, idx) => (
                                      <tr key={idx} className="border-b border-slate-200/50">
                                        <td className="py-1 pr-3 font-mono">{fmtFecha(h.fecha)}</td>
                                        <td className="py-1 pr-3 font-mono">{h.numero_oc || '—'}</td>
                                        <td className="py-1 pr-3 font-mono">{h.numero_recepcion || '—'}</td>
                                        <td className="py-1 pr-3 truncate max-w-[180px]">{h.proveedor_nombre || '—'}</td>
                                        <td className="py-1 pr-3 text-right tabular-nums">{fmtCLP(h.cantidad)}</td>
                                        <td className="py-1 text-right tabular-nums font-semibold">${fmtCLP(h.valor_unitario)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {!searched && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground text-sm">
            Ingresá un código o descripción para ver precios históricos.
          </CardContent>
        </Card>
      )}
    </div>
  );
}
